// enci.log.js

import { toZonedTime, format } from 'date-fns-tz';

/**
 * @typedef {Object} LoggerStyles
 * @property {string} text - Style for regular text logs
 * @property {string} items - Style for item logs
 * @property {string} null - Style for null logs
 * @property {string} error - Style for error logs
 */

/** @type {LoggerStyles} */
const LOGGER_STYLES = Object.freeze({
  text: 'color: #ffffff; background: #000000;',
  items: 'color: gold; background: #16365C;',
  null: 'color: green; background: #000000;',
  error: 'color: #ff92ab; background: #000000;',
});

const DEBUG_MODE = window.appsettings.env === 'Local';
const TIME_ZONE = 'America/New_York';

/**
 * @typedef {Object} DebugLogOptions
 * @property {('text'|'items'|'null'|'error')} [type='text'] - The type of log
 * @property {string} [page=''] - The page where the log is coming from
 * @property {string} [fn=''] - The function where the log is being called
 * @property {Object|null} [obj=null] - An optional object to log for more context
 */

/**
 * Logs a debug message with optional context information.
 * @param {string} message - The main message to log
 * @param {DebugLogOptions} [options={}] - Options to customize the log output
 */
export function debugLog(message, { type = 'text', page = '', fn = '', obj = null } = {}) {
  if (!DEBUG_MODE) return;

  const timestamp = getFormattedTimestamp();
  const logMessage = constructLogMessage(timestamp, page, fn, message);
  const style = LOGGER_STYLES[type] || LOGGER_STYLES.text;

  logToConsole(logMessage, style, obj);
}

/**
 * Gets the current timestamp formatted for the specified timezone.
 * @returns {string} Formatted timestamp
 */
function getFormattedTimestamp() {
  const now = new Date();
  const zonedDate = toZonedTime(now, TIME_ZONE);
  return format(zonedDate, '[yyyy-MM-dd HH:mm:ss.SSS]', { timeZone: TIME_ZONE });
}

/**
 * Constructs the log message string.
 * @param {string} timestamp - Formatted timestamp
 * @param {string} page - Page name
 * @param {string} fn - Function name
 * @param {string} message - Main log message
 * @returns {string} Constructed log message
 */
function constructLogMessage(timestamp, page, fn, message) {
  return page ? `${timestamp} ${page} >> ${fn} >> ${message}` : `${timestamp} ${message}`;
}

/**
 * Logs the message to the console with appropriate styling.
 * @param {string} logMessage - The constructed log message
 * @param {string} style - CSS style string for the log
 * @param {Object|null} obj - Optional object to log
 */
function logToConsole(logMessage, style, obj) {
  if (obj) {
    console.info(`%c${logMessage}`, style, obj);
  } else {
    console.info(`%c${logMessage}`, style);
  }
}
